import { inject, Injectable } from '@angular/core'
import { toSignal } from '@angular/core/rxjs-interop'
import { combineLatest, interval, startWith } from 'rxjs'
import { Store } from '@ngxs/store'
import { SetUserLoggedInStatusRequest } from '@biot-client/capacitor-plugins/dist/measurement/plugin-methods.model'
import MeasurementServicePlugin, {
  deviceAboutToRestart
} from './measurement/measurements-plugin'
import {
  RESTART_IN_TEN_MINUTES_MILLISECONDS,
  RESTART_NOW_MILLISECONDS
} from './constants/time-to-restart'
import { NATIVE_BEING_ALIVE_INTERVAL_MILLISECONDS } from './constants/native-being-alive-interval'
import { NetworkService } from '../services/network.service'
import { LogrocketPFFacade } from '../services/logrocket-pf-facade.service'
import { LOGROCKET_DEVICE_ABOUT_TO_RESTART } from '../constants/logrocket-log-keys'
import { PreferenceState } from '../../store/preference/preference.state'
import { AuthState } from '../../store/auth/auth.state'
import { OrganizationState } from '../../store/organization/organization.state'
import { UserState } from '../../store/user/user.state'

@Injectable({
  providedIn: 'root'
})
export class CapacitorPluginsFacade {
  private readonly store = inject(Store)
  private readonly preferenceState = inject(PreferenceState)
  private readonly authState = inject(AuthState)
  private readonly networkService = inject(NetworkService)
  private readonly logrocketPFFacade = inject(LogrocketPFFacade)

  private readonly isMobile = this.preferenceState.isMobile
  private readonly isOnline = toSignal(this.networkService.isOnlineObs$, {
    initialValue: true
  })
  private readonly isAuthorized$ = this.store.select(AuthState.isAuthenticated)
  private readonly currentOrganization$ = this.store.select(
    OrganizationState.currentOrganization
  )
  private readonly currentUser$ = this.store.select(UserState.currentUser)
  private readonly currentUserDeviceId$ = this.store.select(
    PreferenceState.currentUserDeviceId
  )

  public listenToDeviceAboutToRestart(): void {
    if (!this.isMobile) {
      return
    }

    deviceAboutToRestart().subscribe(data => {
      if (!data) {
        return
      }

      const isLoggedIn = this.authState.isAuthenticated()
      const isOnline = this.isOnline()
      const screenSaver = this.preferenceState.getState().screenSaver

      this.logrocketPFFacade.logrocketTrack(LOGROCKET_DEVICE_ABOUT_TO_RESTART, {
        ...data
      })

      if (!isLoggedIn) {
        this.restartSystemNow()
        return
      }

      if (!isOnline) {
        this.preferenceState.setIsSystemAboutToRestartPopupShown(true)
        return
      }

      if (screenSaver) {
        this.restartSystemNow()
        return
      }

      this.preferenceState.setIsSystemAboutToRestartPopupShown(true)
    })
  }

  public restartSystemNow(): void {
    const screenSaver = this.preferenceState.getState().screenSaver
    const isLoggedIn = this.authState.isAuthenticated()
    MeasurementServicePlugin.rebootDevice({
      restartDelayMs: RESTART_NOW_MILLISECONDS
    })
    this.preferenceState.restartSystem(
      RESTART_NOW_MILLISECONDS,
      this.isOnline(),
      screenSaver,
      isLoggedIn
    )
    this.preferenceState.setIsSystemAboutToRestartPopupShown(false)
  }

  public restartSystemInTenMinutes(): void {
    const screenSaver = this.preferenceState.getState().screenSaver
    const isLoggedIn = this.authState.isAuthenticated()
    MeasurementServicePlugin.rebootDevice({
      restartDelayMs: RESTART_IN_TEN_MINUTES_MILLISECONDS
    })
    this.preferenceState.restartSystem(
      RESTART_IN_TEN_MINUTES_MILLISECONDS,
      this.isOnline(),
      screenSaver,
      isLoggedIn
    )
    this.preferenceState.setIsSystemAboutToRestartPopupShown(false)
  }

  public notifyNativePartAboutBeingAlive(): void {
    if (!this.isMobile) {
      return
    }

    interval(NATIVE_BEING_ALIVE_INTERVAL_MILLISECONDS)
      .pipe(startWith(0))
      .subscribe(() => {
        MeasurementServicePlugin.notifyAboutBeingAlive()
      })
  }

  public monitorAuthorizedStatus(): void {
    if (!this.isMobile) {
      return
    }

    combineLatest([
      this.isAuthorized$.pipe(),
      this.currentOrganization$,
      this.currentUser$,
      this.currentUserDeviceId$
    ])
      .pipe()
      .subscribe(
        ([
          isLoggedIn,
          currentOrganization,
          currentUser,
          currentUserDeviceId
        ]) => {
          const data: SetUserLoggedInStatusRequest = {
            isLoggedIn,
            organizationId: currentOrganization?.id || '',
            organizationName: currentOrganization?.name || '',
            userId: currentUser?.id || '',
            userName:
              (currentUser?.name.lastName || '') +
              ' ' +
              (currentUser?.name.firstName || ''),
            deviceId: currentUserDeviceId || ''
          }
          MeasurementServicePlugin.setUserLoggedInStatus(data)
        }
      )
  }
}
